import { useCallback, useEffect, useRef, useState } from 'react';

/** 화면은 2줄 뷰포트만 보이므로 버퍼가 과도하게 커지지 않게 뒤에서 자름 */
const MAX_CAPTION_CHARS = 1200;
/** 마지막 인식 결과 이후 이 시간 동안 입력이 없으면 자막 표시 비움 */
const CAPTION_IDLE_CLEAR_MS = 5000;

/** TS `lib.dom`에 없는 Web Speech API 최소 타입 */
type SpeechRecResult = { isFinal: boolean; 0: { transcript: string } };
type SpeechRecEvent = { resultIndex: number; results: ArrayLike<SpeechRecResult> };
type SpeechRecErrorEvent = { error: string; message?: string };
type SpeechRecognitionInstance = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecEvent) => void) | null;
  onerror: ((e: SpeechRecErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionInstance) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useSlideLiveCaptionRecognition() {
  const [active, setActive] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionInstance | null>(null);
  const activeRef = useRef(false);
  const finalBufRef = useRef('');
  const idleClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearIdleCaptionTimer = useCallback(() => {
    if (idleClearTimerRef.current != null) {
      clearTimeout(idleClearTimerRef.current);
      idleClearTimerRef.current = null;
    }
  }, []);

  const isSupported = typeof window !== 'undefined' && !!getSpeechRecognitionCtor();

  const stop = useCallback(() => {
    clearIdleCaptionTimer();
    activeRef.current = false;
    const r = recRef.current;
    recRef.current = null;
    if (r) {
      r.onend = null;
      r.onresult = null;
      r.onerror = null;
      try {
        r.stop();
      } catch {
        try {
          r.abort();
        } catch {
          /* noop */
        }
      }
    }
    setActive(false);
    finalBufRef.current = '';
    setText('');
  }, [clearIdleCaptionTimer]);

  const start = useCallback(() => {
    setError(null);
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('이 브라우저에서는 음성 자막을 지원하지 않습니다.');
      return;
    }
    stop();
    const r = new Ctor();
    recRef.current = r;
    r.lang = 'ko-KR';
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (event: SpeechRecEvent) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalBufRef.current += res[0].transcript;
        else interim += res[0].transcript;
      }
      const combined = (finalBufRef.current + interim).trim();
      const sliced =
        combined.length > MAX_CAPTION_CHARS
          ? combined.slice(combined.length - MAX_CAPTION_CHARS)
          : combined;
      setText(sliced);
      clearIdleCaptionTimer();
      idleClearTimerRef.current = setTimeout(() => {
        idleClearTimerRef.current = null;
        finalBufRef.current = '';
        setText('');
      }, CAPTION_IDLE_CLEAR_MS);
    };
    r.onerror = (e: SpeechRecErrorEvent) => {
      if (e.error === 'aborted') return;
      if (e.error === 'no-speech' || e.error === 'audio-capture') return;
      clearIdleCaptionTimer();
      finalBufRef.current = '';
      setText('');
      setError(e.message || e.error);
      activeRef.current = false;
      setActive(false);
    };
    r.onend = () => {
      if (activeRef.current && recRef.current === r) {
        try {
          r.start();
        } catch {
          /* noop */
        }
      }
    };
    activeRef.current = true;
    setActive(true);
    try {
      r.start();
    } catch {
      activeRef.current = false;
      setActive(false);
      clearIdleCaptionTimer();
      setError('음성 인식을 시작할 수 없습니다.');
    }
  }, [stop, clearIdleCaptionTimer]);

  useEffect(() => () => stop(), [stop]);

  return { active, text, error, start, stop, isSupported };
}
